import React, { useContext, useState } from 'react';
import { cloneDeep } from 'lodash';
import { assign } from 'xstate';
import { Box, Slider, Typography } from '@mui/material';
import { IntlShape } from 'react-intl';

import { SendContext } from '../../SendContext';
import { BaseComponent } from '../Component';
import { MachineContext } from '../../MachineContext';
import { getStringValuesParam, translateWithFallback } from '../../util';
import {EvoachButton} from "../Button";

export interface PercentageScaleInputComponent extends BaseComponent {
  type: 'percentageScaleInput';
  props: PercentageScaleInputProps;
}
export interface PercentageScaleInputProps {
  buttonText: string;
  scaleName?: string;
  saveResultTo: string;
  scale?: number;
}
export const PercentageScaleInput: React.FC<PercentageScaleInputProps> = ({
  buttonText,
  scaleName,
  scale,
  saveResultTo,
}) => {
  const { send } = useContext(SendContext);
  const marksSize = 100;

  const marks = [
    {
      value: 0,
      label: '0%',
    },
    {
      value: 50,
      label: '50%',
    },
    {
      value: 100,
      label: '100%',
    },
  ];
  const currentscale = scale ? scale : Math.round(marksSize / 2);
  const [sliderValue, setSliderValue] = useState(currentscale);

  const handleSliderChange = (_event: Event, newValue: number | number[]) => {
    setSliderValue(newValue as number);
  };

  return (
    <Box sx={{ paddingLeft: '25px', paddingRight: '25px' }}>
      <Typography variant="subtitle1">{scaleName}</Typography>
      <div
        style={{
          paddingLeft: '10px',
          paddingRight: '20px',
          paddingTop: '50px',
        }}
      >
        <Slider
          value={sliderValue}
          step={5}
          valueLabelDisplay="on"
          min={0}
          marks={marks}
          max={marksSize}
          onChange={handleSliderChange}
          size="medium"
          getAriaValueText={(value: number) => {
            return `${value}%`;
          }}
          valueLabelFormat={(value: number) => {
            return `${value}%`;
          }}
          sx={{
            '& .MuiSlider-valueLabel': {
              lineHeight: 1.2,
              fontSize: 12,
              background: 'unset',
              padding: 0,
              width: 37,
              height: 37,
              borderRadius: '50% 50% 50% 0',
              backgroundColor: '#4199AC',
              transformOrigin: 'bottom left',
              transform: 'translate(50%, -100%) rotate(-45deg) scale(0)',
              '&:before': { display: 'none' },
              '&.MuiSlider-valueLabelOpen': {
                transform: 'translate(50%, -100%) rotate(-45deg) scale(1)',
              },
              '& > *': {
                transform: 'rotate(45deg)',
              },
            },
          }}
        />
      </div>
      <EvoachButton
        onClick={() => {
          send({
            type: 'writeToContext',
            payload: { [saveResultTo]: sliderValue },
          });
          send({
            type: 'next',
          });
        }}
      >
        {buttonText}
      </EvoachButton>
    </Box>
  );
};

export const renderPercentageScaleInputAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    const getValueFrom = payload.getValueFrom;
    const varsForString: Record<string, any> | undefined = getStringValuesParam(
      payload,
      context
    );
    const scalePreset = getValueFrom
      ? parseInt(context.userData[getValueFrom])
      : parseInt(payload.scale + '');

    newContext.widgetData.push({
      type: 'percentageScaleInput',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        buttonText: translateWithFallback(
          intl,
          payload.buttonText,
          varsForString
        ),
        scaleName: translateWithFallback(
          intl,
          payload.scaleName,
          varsForString
        ),
        scale: scalePreset,
        saveResultTo: payload.saveResultTo,
      },
    });
    return newContext;
  });
};
